import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import Button from "../../../components/Button/Button"
import PlusIcon from "../../../assets/images/icons/PlusIcon.svg"
import DebounceInput from "../../../components/DebouncedSearch/DebouncedSearch"
import TableFooter from "../../../components/Pagination/TableFooter"
import TriggersTable from "./TriggersTable"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { normalizeError } from "../../../utils/errorHelpers"
import { TABLE_CARD } from "../../../components/Table/tableCardClass"
import {
  loadTriggers,
  loadActions,
  deleteTrigger,
  duplicateTrigger,
  selectTriggers,
  selectError
} from "../../../features/workflow/workflowSlice"

const TriggersManagement = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { triggerLoading } = useMyContextLoading()

  const allTriggers = useSelector(selectTriggers)
  const error = useSelector(selectError)

  const [valuePagination, setValuePagination] = useState(10)
  const [pagePagination, setPagePagination] = useState(1)
  const [pageCount, setPageCount] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [triggers, setTriggers] = useState([])

  const fetchTriggers = async () => {
    triggerLoading(true)
    try {
      await dispatch(loadTriggers())
      await dispatch(loadActions())
    } catch (error) {
      showSnackBar("error", normalizeError(error))
    }
    triggerLoading(false)
  }

  useEffect(() => {
    fetchTriggers()
  }, [])

  // Фільтрація тригерів
  useEffect(() => {
    let filtered = allTriggers

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = allTriggers.filter(trigger =>
        trigger.name?.toLowerCase().includes(query) ||
        trigger.condition?.name?.toLowerCase().includes(query) ||
        trigger.action?.some(action => action.name?.toLowerCase().includes(query))
      )
    }

    setTotalCount(filtered.length)

    // Пагінація
    const offset = (pagePagination - 1) * valuePagination
    const paginatedData = filtered.slice(offset, offset + valuePagination)
    setTriggers(paginatedData)

    // Розрахувати кількість сторінок
    const calculatedPageCount = Math.ceil(filtered.length / valuePagination)
    setPageCount(calculatedPageCount)
  }, [allTriggers, searchQuery, valuePagination, pagePagination])

  // Скинути сторінку при зміні пошуку або кількості рядків
  useEffect(() => {
    setPagePagination(1)
  }, [valuePagination, searchQuery])

  const handleSearch = (value) => {
    if (value?.length >= 3) {
      setSearchQuery(value)
    } else if (value?.length === 0) {
      setSearchQuery('')
    }
  }

  const handleAddTrigger = () => {
    navigate("/triggers/add")
  }

  const handleEditTrigger = (id) => {
    navigate(`/triggers/edit/${id}`)
  }

  const handleCopyTrigger = async (id) => {
    triggerLoading(true)
    try {
      await dispatch(duplicateTrigger(id)).unwrap()
      await dispatch(loadTriggers())
      showSnackBar("success", "Trigger duplicated successfully")
    } catch (error) {
      showSnackBar("error", normalizeError(error) || "Failed to duplicate trigger")
    }
    triggerLoading(false)
  }

  const handleDeleteTrigger = async (id, triggerName) => {
    triggerLoading(true)
    try {
      await dispatch(deleteTrigger(id)).unwrap()
      showSnackBar("success", "Trigger deleted successfully")
    } catch (error) {
      showSnackBar("error", normalizeError(error) || "Failed to delete trigger")
    }
    triggerLoading(false)
  }

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <h1 className="font-bold text-xl">Triggers</h1>
        <Button classes="flex h-9 items-center" onClick={handleAddTrigger}>
          <span className="flex items-center gap-2">
            <img src={PlusIcon} alt="plus" />
            <span>Add Trigger</span>
          </span>
        </Button>
      </div>

      {error && (
        <div className="mt-3 mb-3 p-4 bg-error-light border border-error-main text-error-main rounded">
          {normalizeError(error)}
        </div>
      )}

      <div className={`${TABLE_CARD} mt-3`}>
        <div className="flex flex-row justify-between items-center px-5 py-4">
          <DebounceInput
            customClassName={'mr-5 b-1'}
            setSearchValue={handleSearch}
            placeholder="Search..."
          />
        </div>
        <TriggersTable
          data={triggers}
          onEdit={handleEditTrigger}
          onCopy={handleCopyTrigger}
          onDelete={handleDeleteTrigger}
        />
        <TableFooter page={pagePagination} limit={valuePagination} total={totalCount}
          pageCount={pageCount} setPage={setPagePagination} setLimit={setValuePagination} />
      </div>
    </>
  )
}

export default TriggersManagement
